// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Aircraft workspace shell + fleet list + per-panel renderers:
// - renderAircraftWorkspace: header title + photo + summary cards +
//   activity cards + the fleet list with edit/delete actions, then
//   dispatches to the active panel (flights/uploads) renderer.
// - Fleet drag/drop: fleetRowsFromDom + clearAircraftFleetDragState +
//   aircraftRowDropPlacement + persistAircraftFleetOrderFromDom +
//   reorderAircraftFleetRow.
// - Per-flight icon markup helpers.
// - renderAircraftFlightsPanel: the flights table with sort/filter
//   chrome (data layer lives in aircraft-flights-panel-data.js).
// - renderAircraftUploadsPanel: the database uploads table.
//
// Heaviest factory in the codebase (~50 deps) because this is the
// aircraft workspace shell and it touches almost every other panel.

// The "From > To" cell markup (issue #214) lives in flight-route-display.js so
// the column, the PDF report, and the single-flight detail view format the
// persisted origin/arrival identically.
import { applyFlightColumns, setupFlightColumnControls, flightColumnOverrides } from "./flight-columns-controller.js";
import { resolveFlightColumns } from "./flight-table-columns.js";
import { flightRouteMarkup, flightRouteHasEndpoints } from "./flight-route-display.js";
import { flightDateStartLabel } from "./flight-display-model.js";
import {
  newUploadIds,
  uploadsHaveNew,
  setAircraftUploadsHaveNew,
  setAircraftFlightsHaveNew,
} from "./new-item-marks.js";
import {
  aircraftFlightMonitorReference,
  aircraftFlightSourceFilenames,
} from "./aircraft-flights-panel-data.js";

export function flightReanalysisBusyTitle({
  activeAircraft = null,
  viewedAircraft = null,
  aircraftDisplayLabel = (value) => String(value ?? ""),
} = {}) {
  if (!activeAircraft || activeAircraft === viewedAircraft) {
    return "Re-analysis is already running for this aircraft.";
  }
  const activeAircraftLabel = aircraftDisplayLabel(activeAircraft) || "another aircraft";
  return `Re-analysis is already running for ${activeAircraftLabel}. Wait for it to finish before starting another aircraft.`;
}

export function flightAnalysisUpdateAvailable(entry) {
  return flightUpdateAction(entry)?.kind === "reanalyze";
}

export function flightParserUpdateAvailable(entry) {
  const compatibility = entry?.database?.compatibilityStatus;
  return compatibility?.parserUpdate?.available === true
    || compatibility?.status === "parser_update_available";
}

export function flightCanReanalyze(entry) {
  const compatibility = entry?.database?.compatibilityStatus;
  return !flightParserUpdateAvailable(entry) && compatibility?.status !== "incompatible";
}

export function flightUpdateAction(entry) {
  const compatibility = entry?.database?.compatibilityStatus;
  const parserUpdateAvailable = flightParserUpdateAvailable(entry);
  const parserAction = compatibility?.parserUpdate?.action ?? compatibility?.action;
  if (parserUpdateAvailable && parserAction === "reprocess" && entry?.database?.uploadId) {
    return {
      kind: "reprocess",
      label: "Update",
      title: "Rebuild from the original upload using the latest processing.",
    };
  }
  const analysisUpdateAvailable = compatibility?.analysisUpdate?.available === true
    || compatibility?.status === "analysis_update_available";
  const analysisAction = compatibility?.analysisUpdate?.action ?? compatibility?.action;
  if (
    analysisUpdateAvailable
    && analysisAction === "reanalyze"
    && entry?.database?.analysisRunId
    && entry?.database?.flightId
  ) {
    return {
      kind: "reanalyze",
      label: "Update",
      title: "Refresh results using the latest analysis.",
    };
  }
  return null;
}

export function createAircraftWorkspaceRenderer({
  escapeHtml,
  onWorkspaceRendered = () => {},
  formatCompactElapsedTime,
  isSupportedFlight,
  flightDisplayTitle,
  flightDateLabel,
  appReadOnlyMode,
  readOnlyActionMessage,
  flightsReanalyzeAllStatus = () => ({ active: false, aircraft: null }),
  flightsUpdateSelectedBusy = () => false,
  aircraftDisplayLabel,
  aircraftMakeModelLabel,
  aircraftTypeLabel,
  aircraftPhotoSource,
  aircraftPhotoDisplaySrc,
  aircraftPhotoFallbackLabel,
  applyAircraftDefaultArt = () => {},
  defaultArtRestUrl = () => null,
  aircraftProfileHealthLabel,
  aircraftProfileForEntry,
  aircraftFleetRowDetail,
  aircraftRecentFlightCount,
  aircraftActivityCardsForPanel,
  allAircraftOptionsFromFlightMap,
  engineLabel,
  propellerTypeLabel,
  propellerLabel,
  monitorLabel,
  selectedAircraftWorkspace,
  visibleFlightEntriesByAircraft,
  aircraftFlights,
  aircraftFlightsFromMap,
  latestFlightFromAircraftFlights,
  cachedAircraftAverageFlightDurationSeconds,
  reviewMonitorCountText,
  setAircraftPhotoPresentation,
  updateAircraftPhotoControls,
  resetAircraftForm,
  updateAircraftSidebarCard,
  databaseAircraftSummaryFlightCount,
  setAircraftFormStatus,
  renderAircraftMaintenancePanel,
  // flights panel deps
  aircraftFlightsFilters,
  aircraftFlightDateRangeStart,
  sortedAircraftFlightsForPanel,
  aircraftFlightDurationSecondsFromTrendRows,
  aircraftTrendRowsForActivity,
  flightFlagSummary,
  aircraftFlightMatchesFilters,
  updateAircraftFlightSortHeaders,
  updateAircraftFlightsFilterControls,
  renderAircraftFlightsPanelSummary,
  flightReportAvailable,
  flightReportStale = () => false,
  flightAiSummaryAvailable,
  flightAiSummaryStale,
  aiSummaryAvailableForEntry,
  flightAiSummaryUnviewed = () => false,
  flightAutoExcludedFromTrendAnalysis,
  flightIncludedInTrendAnalysis,
  flightTooShortForTrendAnalysis,
  flightConsumedByTrends,
  trendCappedFlightSummary,
  trendFlightCap,
  trendHoursCapSeconds,
  flightFlagSummaryMarkup,
  hydrateAircraftFlightFlagSummaries,
  hydrateAircraftFlightReportMetadata,
  aircraftFlightBulkDeleteSelected = new Set(),
  aircraftFlightDeletePending = new Set(),
  // uploads panel deps
  uploadStatusClass,
  uploadStatusLabel,
  formatUploadByteSize,
  uploadDisplayStatus,
  uploadDisplayError,
  uploadCanReprocess = () => false,
  uploadHasActiveJob,
  uploadReprocessActionLabel,
  uploadIsEmpty,
  uploadFlightSummary,
  uploadFlightSummaryDetailClass = () => "",
  uploadJobProgressLabel,
  uploadDateLabel,
  shortUploadHash,
  loadAircraftUploadHistory,
  aircraftUploadHistoryCache,
  aircraftUploadHistoryPending,
  aircraftUploadHistoryErrors,
  aircraftUploadReprocessPending,
  aircraftUploadReprocessActive = new Set(),
  aircraftUploadProcessShortPending,
  aircraftUploadDeletePending = new Set(),
  aircraftUploadBulkDeleteSelected = new Set(),
  // Called once an aircraft's last active ingest job settles, so the flight
  // manifest picks up the flights that job produced.
  onUploadIngestSettled = async () => {},
  // fleet drag/drop deps
  databaseAircraftEndpoint,
  databaseAircraftProfilesMap,
  aircraftProfilesLocalStorageKey,
  // mutable state
  getAircraftProfiles,
  setAircraftProfiles,
  getLocalAircraftProfiles,
  setLocalAircraftProfiles,
  setDatabaseAircraftRecords,
  getDatabaseAircraftRecords,
  getAircraftFlightManifestReady,
  getSelectedAircraftPanel,
  getAircraftReorderInFlight,
  setAircraftReorderInFlight,
  browserWindow = globalThis.window,
}) {
  // Last markup written to #aircraftFlightsBody, so renderAircraftFlightsPanel
  // can skip byte-identical rebuilds (the hydrate callbacks re-render once their
  // async data settles). Reset on nothing — the firstElementChild guard in the
  // renderer covers an externally cleared body.
  let lastFlightsBodyHtml = null;
  const uploadHistoryRefreshTimers = new Map();

  function clearUploadHistoryRefresh(aircraft) {
    const timer = uploadHistoryRefreshTimers.get(aircraft);
    if (!timer) return;
    const clearTimer = browserWindow?.clearTimeout ?? globalThis.clearTimeout;
    clearTimer?.(timer);
    uploadHistoryRefreshTimers.delete(aircraft);
  }

  function setHas(collection, value) {
    return typeof collection?.has === "function" && collection.has(value);
  }

  // Some callers (and the narrower UI checks) pass a plain object for the
  // upload-history cache, so never assume a Map here.
  function cachedUploadHistory(aircraft) {
    const cached = typeof aircraftUploadHistoryCache?.get === "function"
      ? aircraftUploadHistoryCache.get(aircraft)
      : null;
    return Array.isArray(cached) ? cached : [];
  }

  // Polls the aircraft's upload history while any ingest job is still running.
  // Runs whichever panel is showing (not just Uploads): when the last job
  // settles the new flights are pulled into the manifest via
  // onUploadIngestSettled, so the Flights list fills itself instead of sitting
  // stale until the user reloads the page.
  function scheduleActiveUploadHistoryRefresh(aircraft, uploads = []) {
    if (!aircraft) return;
    const hasActiveUpload = uploads.some((upload) => uploadHasActiveJob(upload));
    if (!hasActiveUpload) {
      clearUploadHistoryRefresh(aircraft);
      return;
    }
    if (uploadHistoryRefreshTimers.has(aircraft) || aircraftUploadHistoryPending.has(aircraft)) return;
    const setTimer = browserWindow?.setTimeout ?? globalThis.setTimeout;
    if (typeof setTimer !== "function") return;
    const timer = setTimer(() => {
      uploadHistoryRefreshTimers.delete(aircraft);
      loadAircraftUploadHistory(aircraft, { force: true })
        .then(async (refreshed = []) => {
          if (refreshed.some((upload) => uploadHasActiveJob(upload))) return;
          await onUploadIngestSettled(aircraft);
        })
        .catch(() => {})
        .finally(() => {
          if (selectedAircraftWorkspace() !== aircraft) return;
          // The settle above pulls the finished upload's flights into the
          // manifest, so the Flights dot has to be repainted here whatever
          // panel is showing. Watching the uploads table while a log finishes
          // is the common path, and renderAircraftFlightsPanel does not run
          // there to do it for us.
          setAircraftFlightsHaveNew(aircraftFlights(aircraft).some((entry) => entry?.is_new === true));
          if (getSelectedAircraftPanel() === "uploads") {
            renderAircraftUploadsPanel(aircraft);
            return;
          }
          // On any other panel the Uploads table is not rendered, so refresh
          // the tab dot straight from the history this poll just loaded.
          // Without this an upload finished from the Flights tab stays
          // unbadged until Uploads is opened.
          setAircraftUploadsHaveNew(
            aircraft,
            uploadsHaveNew(aircraft, cachedUploadHistory(aircraft).map((upload) => ({ id: String(upload?.id ?? ""), uploadedAt: upload?.uploaded_at }))),
          );
        });
    }, 1500);
    uploadHistoryRefreshTimers.set(aircraft, timer);
  }

  function syncUploadReprocessSelectedButton({
    hidden = true,
    disabled = true,
    count = 0,
    title = "",
  } = {}) {
    const button = document.querySelector("#aircraftUploadReprocessAll");
    if (!button) return;
    button.hidden = hidden;
    button.disabled = disabled;
    button.textContent = count > 0 ? `Reprocess Selected (${count})` : "Reprocess Selected";
    button.title = title;
    button.setAttribute(
      "aria-label",
      count > 0 ? `Reprocess ${count} selected upload${count === 1 ? "" : "s"}` : "Reprocess selected uploads",
    );
  }

  function syncUploadBulkDeleteControls({
    hidden = true,
    disabled = true,
    selectAllDisabled = disabled,
    selectedCount = 0,
    selectableCount = 0,
    title = "",
  } = {}) {
    const button = document.querySelector("#aircraftUploadsDeleteSelected");
    if (button) {
      button.hidden = hidden;
      button.disabled = disabled;
      button.textContent = selectedCount > 0 ? `Delete Selected (${selectedCount})` : "Delete Selected";
      button.title = title;
      button.setAttribute(
        "aria-label",
        selectedCount > 0 ? `Delete ${selectedCount} selected upload${selectedCount === 1 ? "" : "s"}` : "Delete selected uploads",
      );
    }
    const selectAll = document.querySelector("#aircraftUploadsSelectAll");
    if (selectAll) {
      selectAll.disabled = selectableCount === 0 || selectAllDisabled;
      selectAll.checked = selectableCount > 0 && selectedCount === selectableCount;
      selectAll.indeterminate = selectedCount > 0 && selectedCount < selectableCount;
      selectAll.title = selectableCount > 0 ? "Select all uploads in this table" : "No uploads available to select";
    }
  }

  function aircraftUsesCgrMonitor(aircraft) {
    const profile = aircraftProfileForEntry({ aircraft }) ?? getAircraftProfiles()?.[aircraft] ?? null;
    const monitorText = [
      profile?.monitor?.type,
      profile?.monitor?.make,
      profile?.monitor?.model,
      monitorLabel(profile),
    ].filter(Boolean).join(" ");
    return /\bCGR[-\s]?30[PC]?\b/i.test(monitorText);
  }

  function aircraftFlightCgrSource(entry, enabled = true) {
    if (!enabled) return null;
    const parser = String(entry?.parser ?? entry?.detected_parser ?? "").toLowerCase();
    const monitor = String(entry?.monitor ?? entry?.detected_monitor ?? "").trim();
    const components = Array.isArray(entry?.components) ? entry.components : [];
    const componentText = components.map((component) => `${component?.monitor ?? ""} ${component?.role ?? ""}`).join(" ");
    if (parser === "ei-cgr-twin") {
      const sources = entry.cgrTwin?.sources ?? components;
      const count = sources.filter(source => source.role === "left" || source.role === "right").length;
      const shared = sources.some(source => source.role === "shared");
      return { label: `${count} x 30P${shared ? " + 30C" : ""}`, className: count === 2 ? "is-paired" : "is-unmatched", title: count === 2 ? "Separate CGR engine recordings" : "Waiting for the other engine recording" };
    }
    const hasPrimary = parser === "ei-cgr"
      || /\bCGR[-\s]?30P\b/i.test(monitor)
      || /\bCGR[-\s]?30P\b/i.test(componentText)
      || components.some((component) => component?.role === "primary");
    const hasAuxiliary = parser === "ei-cgr-30c"
      || parser === "ei-cgr-30c-legacy"
      || /\bCGR[-\s]?30C\b/i.test(monitor)
      || /\bCGR[-\s]?30C\b/i.test(componentText)
      || components.some((component) => component?.role === "auxiliary")
      || Boolean(entry?.auxPath);
    if (parser === "ei-cgr-combo" || (hasPrimary && hasAuxiliary)) {
      return { label: "30P+30C", className: "is-paired", title: "Matched CGR-30P and CGR-30C files" };
    }
    if (hasAuxiliary) return { label: "30C", className: "is-unmatched", title: "CGR-30C file without a matched CGR-30P file" };
    if (hasPrimary) return { label: "30P", className: "is-unmatched", title: "CGR-30P file without a matched CGR-30C file" };
    return { label: "--", className: "is-empty", title: "No CGR source for this flight" };
  }

  function syncAircraftFlightsCgrColumn(showCgrSource) {
    const table = document.querySelector(".aircraft-flights-table");
    const cgrHeader = document.querySelector("[data-aircraft-flight-cgr-column]");
    table?.classList?.toggle("has-cgr-column", Boolean(showCgrSource));
    if (cgrHeader) cgrHeader.hidden = !showCgrSource;
  }

  function syncAircraftFlightsAnalysisUpdateColumn(showAnalysisUpdate, updateCount = 0) {
    const table = document.querySelector(".aircraft-flights-table");
    const updateHeader = document.querySelector("[data-aircraft-flight-analysis-update-column]");
    table?.classList?.toggle("has-analysis-update-column", Boolean(showAnalysisUpdate));
    if (updateHeader) {
      updateHeader.hidden = !showAnalysisUpdate;
      updateHeader.textContent = showAnalysisUpdate ? `Update · ${updateCount}` : "Update";
    }
  }

  function renderAircraftWorkspace() {
    const title = document.querySelector("#aircraftWorkspaceTitle");
    const photo = document.querySelector("#aircraftWorkspaceImage");
    const photoFallback = document.querySelector("#aircraftWorkspaceImageFallback");
    const summaryGrid = document.querySelector("#aircraftSummaryGrid");
    const activityGrid = document.querySelector("#aircraftActivityGrid");
    const fleetList = document.querySelector("#aircraftFleetList");
    if (!title || !summaryGrid || !activityGrid || !fleetList) return;

    const aircraft = selectedAircraftWorkspace();
    const aircraftProfiles = getAircraftProfiles();
    const profile = aircraftProfileForEntry({ aircraft }) ?? aircraftProfiles?.[aircraft] ?? null;
    const photoDataUrl = aircraftPhotoSource(profile);
    const fleetFlightsByAircraft = visibleFlightEntriesByAircraft();
    const flights = aircraftFlightsFromMap(fleetFlightsByAircraft, aircraft);
    const latestFlight = latestFlightFromAircraftFlights(flights);
    const aircraftMakeModel = aircraftMakeModelLabel(profile);
    const makeModelText = aircraftMakeModel ?? "Make/model not set";
    const engineText = engineLabel(profile) ?? "Engine not set";
    const propellerTypeText = propellerTypeLabel(profile);
    const propellerHardwareText = propellerLabel(profile);
    const propellerText = [propellerTypeText, propellerHardwareText].filter(Boolean).join(" · ") || "Propeller not set";
    // Resolve the header monitor exactly like the fleet row does: scan all
    // flights for a usable monitor, skip format-only parser names, and fall
    // back to "Monitor not set" (monitorLabel alone would surface "Unknown").
    const monitorText = aircraftFleetRowDetail(profile, flights, latestFlight).monitor;
    const flightDateText = latestFlight ? flightDateLabel(null, latestFlight) : "No flights yet";
    const recentFlightCount = aircraftRecentFlightCount(flights, 90);
    const averageFlightDuration = cachedAircraftAverageFlightDurationSeconds(aircraft, flights);
    const setupStatus = aircraftProfileHealthLabel(profile, latestFlight);
    const readOnly = appReadOnlyMode();

    const summaryCards = aircraft ? [
      {
        label: "Make/Model",
        value: makeModelText,
        actionField: aircraftMakeModel ? null : "aircraftMakeInput",
        actionMessage: aircraftMakeModel ? "" : `Add the make and model for ${aircraftDisplayLabel(aircraft)} below.`,
      },
      {
        label: "Propeller",
        value: propellerText,
        actionField: propellerTypeText ? null : "propellerType",
        actionMessage: propellerTypeText ? "" : `Choose the propeller type for ${aircraftDisplayLabel(aircraft)} below.`,
      },
      {
        label: "Engine",
        value: engineText,
        actionField: engineLabel(profile) ? null : "aircraftEngineProfileInput",
        actionMessage: engineLabel(profile) ? "" : `Add the engine profile for ${aircraftDisplayLabel(aircraft)} below.`,
      },
      {
        label: "Monitor",
        value: monitorText,
        actionField: null,
        actionMessage: "",
      },
      {
        label: "Review Monitors",
        value: reviewMonitorCountText(aircraft),
        actionField: null,
        actionMessage: "",
      },
    ] : [];

    title.textContent = aircraft ? aircraftDisplayLabel(aircraft) : "No aircraft available";
    const reviewSettingsButton = document.querySelector("#aircraftReviewSettingsButton");
    if (reviewSettingsButton) {
      reviewSettingsButton.disabled = !aircraft;
      reviewSettingsButton.title = aircraft ? `Review settings for ${aircraftDisplayLabel(aircraft)}` : "Select an aircraft first";
    }
    setAircraftPhotoPresentation(photo, photoFallback, photoDataUrl, aircraft);
    updateAircraftPhotoControls(aircraft, photoDataUrl);
    applyAircraftDefaultArt({
      imageElement: photo,
      fallbackElement: photoFallback,
      frameElement: document.querySelector("#aircraftWorkspacePhotoTrigger"),
      profile,
      hasUserPhoto: Boolean(photoDataUrl),
    });
    summaryGrid.innerHTML = aircraft ? summaryCards.map((card) => `
      <article class="aircraft-summary-card${card.actionField ? " is-actionable" : ""}"${card.actionField ? ` data-aircraft-form-field="${escapeHtml(card.actionField)}" data-aircraft-form-message="${escapeHtml(card.actionMessage)}" tabindex="0" role="button" aria-label="${escapeHtml(`${card.label}. ${card.value}`)}"` : ""}>
        <span>${escapeHtml(card.label)}</span>
        <strong>${escapeHtml(card.value)}</strong>
      </article>
    `).join("") : "";

    const activityCards = aircraftActivityCardsForPanel({
      aircraft,
      flights,
      latestFlight,
      flightDateText,
      recentFlightCount,
      averageFlightDuration,
      setupStatus,
      makeModelText,
    });

    activityGrid.innerHTML = activityCards.map((card) => `
      <article class="aircraft-activity-card aircraft-activity-card-${escapeHtml(card.accent)} aircraft-activity-card-${escapeHtml(card.tone)}">
        <span>${escapeHtml(card.label)}</span>
        <strong>${escapeHtml(card.value)}</strong>
        <small>${escapeHtml(card.detail)}</small>
      </article>
    `).join("");
    const flightsMetricsGrid = document.querySelector("#aircraftFlightsMetricsGrid");
    if (flightsMetricsGrid) {
      // With no flights every card is an empty-state placeholder ("Dates
      // unavailable", "--", "0/0"), so the whole summary panel comes down
      // rather than showing a strip of zeros.
      flightsMetricsGrid.hidden = !flights.length;
      flightsMetricsGrid.innerHTML = activityCards.map((card) => `
        <article class="aircraft-activity-card aircraft-activity-card-${escapeHtml(card.accent)} aircraft-activity-card-${escapeHtml(card.tone)}">
          <span>${escapeHtml(card.label)}</span>
          <strong>${escapeHtml(card.value)}</strong>
          <small>${escapeHtml(card.detail)}</small>
        </article>
      `).join("");
    }

    const aircraftRows = allAircraftOptionsFromFlightMap(fleetFlightsByAircraft);
    fleetList.innerHTML = aircraftRows.length ? aircraftRows.map((item) => {
      const itemProfile = aircraftProfiles?.[item] ?? null;
      const itemPlanLocked = itemProfile?.planAccess?.locked === true;
      const itemPlanMessage = String(itemProfile?.planAccess?.message ?? "This aircraft is retained but locked by the account plan.");
      // Resolve through the photo blob cache: the DB summary hands us a JWT-gated
      // /api/aircraft/:id/photo URL that an <img src> can't authenticate, so this
      // returns the cached blob: URL (or null until the auth-aware fetch lands and
      // triggers a re-render). Fresh-upload data: URLs pass straight through.
      const itemPhotoDataUrl = aircraftPhotoDisplaySrc(aircraftPhotoSource(itemProfile));
      const itemFlights = aircraftFlightsFromMap(fleetFlightsByAircraft, item);
      const itemLatestFlight = latestFlightFromAircraftFlights(itemFlights);
      const rowDetail = aircraftFleetRowDetail(itemProfile, itemFlights, itemLatestFlight);
      const itemSummaryFlightCount = !getAircraftFlightManifestReady() ? databaseAircraftSummaryFlightCount(item) : null;
      if (!itemFlights.length && itemSummaryFlightCount) {
        rowDetail.logs = `${itemSummaryFlightCount} log${itemSummaryFlightCount === 1 ? "" : "s"}`;
      }
      const itemMakeModelText = aircraftMakeModelLabel(itemProfile) ?? "Make/model not set";
      const selected = item === aircraft;
      const archiveTitle = readOnly
        ? readOnlyActionMessage("Archiving aircraft")
        : `Archive ${aircraftDisplayLabel(item)} (moves it to the Archive tab in My Account)`;
      return `
        <article class="aircraft-fleet-item ${selected ? "is-selected" : ""}${itemPlanLocked ? " is-plan-locked" : ""}" data-aircraft-row="${escapeHtml(item)}" data-aircraft-id="${escapeHtml(itemProfile?.databaseId ?? "")}"${itemPlanLocked ? ' data-plan-locked="true" aria-disabled="true"' : ""} role="group" tabindex="0" draggable="false" aria-label="${itemPlanLocked ? "Unavailable" : "Select"} ${escapeHtml(aircraftDisplayLabel(item))} aircraft row${itemPlanLocked ? ". Plan limit reached" : ""}${readOnly ? ". Reordering is disabled in demo mode" : ". Drag to reorder"}."${selected ? ' aria-current="true"' : ""}>
          <div class="aircraft-fleet-photo">
            ${itemPhotoDataUrl
              ? `<img src="${escapeHtml(itemPhotoDataUrl)}" alt="${escapeHtml(`${aircraftDisplayLabel(item)} aircraft photo`)}" draggable="false" />`
              : (() => {
                const itemDefaultArt = defaultArtRestUrl(itemProfile);
                return itemDefaultArt
                  ? `<img class="is-default-art" src="${escapeHtml(itemDefaultArt)}" alt="${escapeHtml(`${aircraftDisplayLabel(item)} aircraft line art`)}" draggable="false" />`
                  : `<div class="aircraft-photo-fallback">${escapeHtml(aircraftPhotoFallbackLabel(item))}</div>`;
              })()}
          </div>
          <div class="aircraft-fleet-copy">
            <strong>${escapeHtml(aircraftDisplayLabel(item))}${itemPlanLocked ? `<span class="aircraft-fleet-plan-lock" title="${escapeHtml(itemPlanMessage)}">Plan limit</span>` : ""}</strong>
            <dl class="aircraft-fleet-meta" aria-label="${escapeHtml(`${aircraftDisplayLabel(item)} aircraft details`)}">
              <div>
                <dt>Make / Model</dt>
                <dd>${escapeHtml(itemMakeModelText)}</dd>
              </div>
              <div>
                <dt>Engine Model</dt>
                <dd>${escapeHtml(rowDetail.engine)}</dd>
              </div>
              <div>
                <dt>Engine</dt>
                <dd>${escapeHtml(aircraftTypeLabel(itemProfile) ?? "Pending")}</dd>
              </div>
              <div>
                <dt>Monitor</dt>
                <dd>${escapeHtml(rowDetail.monitor)}</dd>
              </div>
              <div>
                <dt>Logs</dt>
                <dd>${escapeHtml(rowDetail.logs)}</dd>
              </div>
            </dl>
          </div>
          <div class="aircraft-fleet-actions">
            <button class="secondary-action icon-action aircraft-edit-action" data-aircraft-edit="${escapeHtml(item)}" type="button" aria-label="Edit ${escapeHtml(aircraftDisplayLabel(item))}" title="${escapeHtml(itemPlanLocked ? itemPlanMessage : "Edit aircraft")}"${itemPlanLocked ? " disabled" : ""}>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
            </button>
            <button class="secondary-action icon-action aircraft-archive-action" data-aircraft-archive="${escapeHtml(item)}" type="button" aria-label="Archive ${escapeHtml(aircraftDisplayLabel(item))}" title="${escapeHtml(archiveTitle)}"${readOnly ? " disabled" : ""}>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <rect x="3" y="4" width="18" height="4" rx="1" />
                <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
                <path d="M10 12h4" />
              </svg>
            </button>
          </div>
        </article>
      `;
    }).join("") : '<p class="aircraft-fleet-empty">No aircraft found yet. Use the plus button or upload a log to create the first aircraft context.</p>';

    resetAircraftForm(profile ? { ...profile, tailNumber: aircraft } : aircraft ? { tailNumber: aircraft } : null, document.querySelector("#aircraftEditDialogForm"));
    const panel = getSelectedAircraftPanel();
    if (panel === "flights") {
      renderAircraftFlightsPanel(aircraft, flights);
    }
    if (panel === "uploads") {
      renderAircraftUploadsPanel(aircraft);
    }
    if (panel === "maintenance") {
      renderAircraftMaintenancePanel(aircraft);
    }
    // Keep the ingest poll alive on every panel. renderAircraftUploadsPanel
    // arms it with its own freshly-read list; from here we use whatever the
    // cache holds, which is enough to know a job is still running.
    if (panel !== "uploads") {
      scheduleActiveUploadHistoryRefresh(aircraft, cachedUploadHistory(aircraft));
      // The Uploads panel renders only while its own tab is showing, so the
      // tab dot has to be refreshed from here too. Otherwise an upload made
      // from the Flights tab stays unbadged until you click Uploads. Read-only
      // (no baseline seeding): the panel itself owns that.
      setAircraftUploadsHaveNew(
        aircraft,
        uploadsHaveNew(aircraft, cachedUploadHistory(aircraft).map((upload) => ({ id: String(upload?.id ?? ""), uploadedAt: upload?.uploaded_at }))),
      );
    }
    // Same story for the Flights dot: renderAircraftFlightsPanel owns it, but
    // that only runs while the Flights tab is showing, so a flight that became
    // new while you sat on Uploads (the common case — you upload, then watch
    // the uploads table) left the tab unbadged until you clicked it. The
    // manifest already carries the server-decorated is_new, so read it here.
    if (panel !== "flights") {
      setAircraftFlightsHaveNew(aircraftFlights(aircraft).some((entry) => entry?.is_new === true));
    }
    updateAircraftSidebarCard();
    // New UI fleet sidebar: repaint alongside every workspace repaint so the
    // rail always reflects freshly loaded aircraft/flight data (the initial
    // authed manifest load has no other repaint signal it could hook).
    onWorkspaceRendered();
  }

  function fleetRowsFromDom() {
    return Array.from(document.querySelectorAll("#aircraftFleetList [data-aircraft-row]"));
  }

  function clearAircraftFleetDragState() {
    fleetRowsFromDom().forEach((row) => {
      row.classList.remove("is-dragging", "is-drop-before", "is-drop-after");
    });
  }

  function aircraftRowDropPlacement(row, clientY) {
    const rect = row.getBoundingClientRect();
    return clientY < rect.top + rect.height / 2 ? "before" : "after";
  }

  async function persistAircraftFleetOrderFromDom() {
    if (appReadOnlyMode()) throw new Error(readOnlyActionMessage("Reordering aircraft"));
    const rows = fleetRowsFromDom();
    const labels = rows.map((row) => row.dataset.aircraftRow).filter(Boolean);
    if (!labels.length) return;

    const aircraftIds = rows.map((row) => row.dataset.aircraftId).filter(Boolean);
    if (aircraftIds.length !== labels.length) {
      throw new Error("Every aircraft needs a database id before the fleet order can be saved.");
    }
    const response = await fetch(`${databaseAircraftEndpoint}/reorder`, {
      method: "POST",
      headers: { "content-type": "application/json; charset=utf-8" },
      body: JSON.stringify({ aircraftIds }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.error || `Database aircraft reorder API HTTP ${response.status}`);
    }
    setDatabaseAircraftRecords(Array.isArray(payload.aircraft) ? payload.aircraft : getDatabaseAircraftRecords());
    setAircraftProfiles(databaseAircraftProfilesMap());
  }

  async function reorderAircraftFleetRow(draggedAircraft, targetRow, placement) {
    const list = document.querySelector("#aircraftFleetList");
    const draggedRow = list?.querySelector(`[data-aircraft-row="${CSS.escape(draggedAircraft)}"]`);
    if (!list || !draggedRow || !targetRow || draggedRow === targetRow || getAircraftReorderInFlight()) return;
    setAircraftReorderInFlight(true);
    const nextSibling = placement === "after" ? targetRow.nextElementSibling : targetRow;
    list.insertBefore(draggedRow, nextSibling);
    clearAircraftFleetDragState();
    try {
      await persistAircraftFleetOrderFromDom();
      renderAircraftWorkspace();
      setAircraftFormStatus("Fleet order saved.", "success");
    } catch (error) {
      setAircraftFormStatus(error.message, "error");
      renderAircraftWorkspace();
    } finally {
      setAircraftReorderInFlight(false);
    }
  }

  function aircraftFlightAiSummaryIconMarkup() {
    return `
      <span class="aircraft-flight-ai-summary-icon" aria-hidden="true">
        <span class="aircraft-flight-icon-mark">AI</span>
        <span class="aircraft-flight-ai-summary-icon-sparkle"></span>
      </span>
    `;
  }

  function aircraftFlightPdfIconMarkup() {
    return `
      <span class="aircraft-flight-pdf-icon" aria-hidden="true">
        <span class="aircraft-flight-icon-mark">PDF</span>
      </span>
    `;
  }

  function aircraftFlightRegenerateIconMarkup() {
    return `
      <span class="aircraft-flight-regenerate-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M21 12a9 9 0 1 1-2.64-6.36" />
          <path d="M21 3v7h-7" />
        </svg>
      </span>
    `;
  }

  function aircraftFlightReanalyzeIconMarkup() {
    return `
      <span class="aircraft-flight-reanalyze-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M3 12a9 9 0 0 1 15.5-6.36" />
          <path d="M21 3v6h-6" />
          <path d="M21 12a9 9 0 0 1-15.5 6.36" />
          <path d="M3 21v-6h6" />
        </svg>
      </span>
    `;
  }

  function syncFlightsUpdateSelectedButton({
    hidden = true,
    disabled = true,
    count = 0,
    title = "",
  } = {}) {
    const button = document.querySelector("#aircraftFlightsUpdateSelected");
    if (!button) return;
    const label = button.querySelector(".aircraft-flights-reanalyze-all-label");
    button.hidden = hidden;
    button.disabled = disabled;
    if (label) label.textContent = count > 0 ? `Update Selected (${count})` : "Update Selected";
    button.title = title;
    button.setAttribute(
      "aria-label",
      count > 0 ? `Update ${count} selected flight${count === 1 ? "" : "s"}` : "Update selected flights",
    );
  }

  function syncFlightsBulkDeleteControls({
    hidden = true,
    disabled = true,
    selectAllDisabled = disabled,
    selectedCount = 0,
    selectableCount = 0,
    title = "",
  } = {}) {
    const button = document.querySelector("#aircraftFlightsArchiveSelected");
    if (button) {
      button.hidden = hidden;
      button.disabled = disabled;
      button.textContent = selectedCount > 0 ? `Archive Selected (${selectedCount})` : "Archive Selected";
      button.title = title;
      button.setAttribute(
        "aria-label",
        selectedCount > 0 ? `Archive ${selectedCount} selected flight${selectedCount === 1 ? "" : "s"}` : "Archive selected flights",
      );
    }
    const selectAll = document.querySelector("#aircraftFlightsSelectAll");
    if (selectAll) {
      selectAll.disabled = selectableCount === 0 || selectAllDisabled;
      selectAll.checked = selectableCount > 0 && selectedCount === selectableCount;
      selectAll.indeterminate = selectedCount > 0 && selectedCount < selectableCount;
      selectAll.title = selectableCount > 0 ? "Select all visible flights" : "No visible flights available to select";
    }
  }

  function renderAircraftFlightsPanel(aircraft, flights = aircraftFlights(aircraft)) {
    const body = document.querySelector("#aircraftFlightsBody");
    if (!body) return;
    const readOnly = appReadOnlyMode();
    const flightPlanLocked = (entry) => entry?.database?.planAccess?.locked === true;
    const showCgrSource = aircraftUsesCgrMonitor(aircraft);
    // Base rows render 12 cells (select, open, date, name, route, duration,
    // flags, trend, ai, report, actions, spacer). CGR and Update are
    // independent conditional columns. Update visibility is based on the
    // complete supported aircraft history so filters cannot make the table
    // shift. Over-counting relative to picker-hidden columns is harmless —
    // the colspan row still spans the full table.
    let columnCount = showCgrSource ? 13 : 12;
    syncAircraftFlightsCgrColumn(showCgrSource);
    const trendRows = aircraftTrendRowsForActivity(aircraft) ?? [];
    const trendHoursCapLabel = `${Math.round(trendHoursCapSeconds / 3600)} hrs`;
    const filters = aircraftFlightsFilters();
    const query = filters.query.trim().toLowerCase();
    const dateStart = aircraftFlightDateRangeStart(flights, filters.dateRange);
    // Drives where the name-edit pencil lives. Whenever the Name COLUMN is
    // visible (auto-shown because a flight is named, or toggled on by hand)
    // the pencil renders there; only while it is hidden does the pencil stay
    // in the Date cell — otherwise there would be no way to name the FIRST
    // flight.
    const anyNamed = flights.some((candidate) => String(candidate?.custom_name ?? "").trim().length > 0);
    const nameColumnVisible = resolveFlightColumns(flightColumnOverrides(), { anyNamed }).name !== false;
    const flightRows = sortedAircraftFlightsForPanel(flights).map((entry) => {
      const duration = aircraftFlightDurationSecondsFromTrendRows(entry, trendRows);
      const durationLabel = Number.isFinite(duration) ? formatCompactElapsedTime(duration) : "--";
      const summary = flightFlagSummary(entry);
      return { entry, durationLabel, summary };
    });
    // Tab dot reflects the whole aircraft, not the current filter: hiding a new
    // flight behind a search box should not make the tab look caught up.
    setAircraftFlightsHaveNew(flights.some((entry) => entry?.is_new === true));
    // Initialize the toolbar state before the filtered-row selection count is
    // resolved; the selection-aware sync below immediately applies the final
    // count, disabled state, and tooltip.
    const reanalyzeAllStatus = flightsReanalyzeAllStatus();
    const reanalyzeAllBusy = reanalyzeAllStatus?.active === true;
    const updateSelectedBusy = flightsUpdateSelectedBusy() === true;
    const reanalyzeAllBusyTitle = flightReanalysisBusyTitle({
      activeAircraft: reanalyzeAllStatus?.aircraft,
      viewedAircraft: aircraft,
      aircraftDisplayLabel,
    });
    const compatibilityNotice = document.querySelector("#aircraftAnalysisUpdateNotice");
    if (compatibilityNotice) {
      compatibilityNotice.hidden = true;
      compatibilityNotice.classList.remove("is-analysis-update");
      compatibilityNotice.innerHTML = "";
    }
    syncFlightsUpdateSelectedButton();
    const sortedFlights = flightRows.filter(({ entry, durationLabel, summary }) => aircraftFlightMatchesFilters(entry, {
      query,
      flags: filters.flags,
      trend: filters.trend,
      dateStart,
      durationLabel,
      summary,
    }));
    const actionableUpdateCount = flightRows.filter(({ entry }) => (
      isSupportedFlight(entry) && Boolean(flightUpdateAction(entry))
    )).length;
    const showAnalysisUpdate = actionableUpdateCount > 0;
    syncAircraftFlightsAnalysisUpdateColumn(showAnalysisUpdate, actionableUpdateCount);
    if (showAnalysisUpdate) columnCount += 1;
    const selectableFlightIds = sortedFlights
      .filter(({ entry }) => entry?.database?.flightId && !setHas(aircraftFlightDeletePending, entry.id))
      .map(({ entry }) => entry.id);
    const selectedFlightCount = selectableFlightIds.filter((id) => setHas(aircraftFlightBulkDeleteSelected, id)).length;
    const flightDeleteBusy = sortedFlights.some(({ entry }) => setHas(aircraftFlightDeletePending, entry.id));
    const selectedReanalyzableCount = sortedFlights.filter(({ entry }) => (
      entry?.database?.flightId
      && isSupportedFlight(entry)
      && flightCanReanalyze(entry)
      && !flightPlanLocked(entry)
      && !setHas(aircraftFlightDeletePending, entry.id)
      && setHas(aircraftFlightBulkDeleteSelected, entry.id)
    )).length;
    const selectedReprocessableCount = sortedFlights.filter(({ entry }) => (
      entry?.database?.flightId
      && entry?.database?.uploadId
      && isSupportedFlight(entry)
      && flightUpdateAction(entry)?.kind === "reprocess"
      && !flightPlanLocked(entry)
      && !setHas(aircraftFlightDeletePending, entry.id)
      && !setHas(aircraftUploadReprocessPending, entry.database.uploadId)
      && setHas(aircraftFlightBulkDeleteSelected, entry.id)
    )).length;
    const selectedUpdatableCount = selectedReprocessableCount + selectedReanalyzableCount;
    const updateSelectedTitle = [
      selectedReprocessableCount
        ? `Rebuild ${selectedReprocessableCount} from original uploads.`
        : "",
      selectedReanalyzableCount
        ? `Refresh analysis for ${selectedReanalyzableCount} from saved flight data.`
        : "",
    ].filter(Boolean).join(" ");
    syncFlightsUpdateSelectedButton({
      hidden: !aircraft || selectedUpdatableCount === 0,
      disabled: readOnly || reanalyzeAllBusy || updateSelectedBusy || selectedUpdatableCount === 0,
      count: selectedUpdatableCount,
      title: readOnly
        ? readOnlyActionMessage("Updating flights")
        : updateSelectedBusy
          ? "Selected flight updates are already running."
        : reanalyzeAllBusy
          ? reanalyzeAllBusyTitle
          : updateSelectedTitle,
    });
    // "Mark Not New" appears only when the selection actually contains an
    // unreviewed flight, so it stays out of the way in the common case.
    const selectedNewCount = flights.filter((entry) => (
      entry?.is_new === true && setHas(aircraftFlightBulkDeleteSelected, entry.id)
    )).length;
    const markSeenButton = document.querySelector("#aircraftFlightsMarkSeen");
    if (markSeenButton) {
      markSeenButton.hidden = selectedNewCount === 0;
      // "NEW" is rendered as the same pill the rows use, so the button names
      // the thing it removes. Count matches the sibling bulk buttons.
      markSeenButton.innerHTML = `Clear <span class="row-new-tag">New</span>${
        selectedNewCount > 1 ? ` (${selectedNewCount})` : ""}`;
      markSeenButton.title = selectedNewCount === 1
        ? "Clear the New tag on the selected flight."
        : `Clear the New tag on ${selectedNewCount} selected flights.`;
      markSeenButton.setAttribute(
        "aria-label",
        selectedNewCount === 1
          ? "Clear the New tag on the selected flight"
          : `Clear the New tag on ${selectedNewCount} selected flights`,
      );
    }
    syncFlightsBulkDeleteControls({
      hidden: !aircraft || selectedFlightCount === 0,
      disabled: readOnly || flightDeleteBusy || selectedFlightCount === 0,
      selectAllDisabled: readOnly || flightDeleteBusy,
      selectedCount: selectedFlightCount,
      selectableCount: selectableFlightIds.length,
      title: readOnly
        ? readOnlyActionMessage("Deleting flights")
        : flightDeleteBusy
          ? "Flight deletion is already running."
          : selectedFlightCount
            ? `Delete ${selectedFlightCount} selected flight${selectedFlightCount === 1 ? "" : "s"}.`
            : selectableFlightIds.length
              ? "Select flights to enable bulk delete."
              : "No visible database-backed flights can be deleted.",
    });
    updateAircraftFlightSortHeaders();
    updateAircraftFlightsFilterControls();
    renderAircraftFlightsPanelSummary(flights, sortedFlights.map((row) => row.entry));
    // When the aircraft has no flights at all (not merely filtered out), swap the
    // table + filter toolbar for the same "No flights yet" empty state the
    // dashboard shows, so a brand-new aircraft's Flights tab isn't an empty grid.
    const flightsPanel = document.querySelector("#aircraftFlightsPanel");
    const flightsEmptyState = document.querySelector("#flightsEmptyAircraftState");
    const flightsEmptyTitle = document.querySelector("#flightsEmptyAircraftTitle");
    const flightsTableWrap = flightsPanel?.querySelector(".table-wrap");
    const flightsToolbar = flightsPanel?.querySelector(".aircraft-flights-toolbar");
    const aircraftHasNoFlights = Boolean(aircraft) && flights.length === 0;
    if (flightsEmptyState) flightsEmptyState.hidden = !aircraftHasNoFlights;
    if (flightsTableWrap) flightsTableWrap.hidden = aircraftHasNoFlights;
    if (flightsToolbar) flightsToolbar.hidden = aircraftHasNoFlights;
    if (aircraftHasNoFlights && flightsEmptyTitle) {
      flightsEmptyTitle.textContent = `No flights yet for ${aircraftDisplayLabel(aircraft)}`;
    }
    const trendCapSummary = trendCappedFlightSummary(aircraft);
    const trendCapNote = document.querySelector("#aircraftFlightsTrendCap");
    if (trendCapNote) {
      if (!trendCapSummary.total) {
        trendCapNote.hidden = true;
        trendCapNote.textContent = "";
        trendCapNote.classList.remove("is-capped");
      } else {
        const usedTime = formatCompactElapsedTime(trendCapSummary.seconds);
        const hoursText = trendCapSummary.hoursKnown ? usedTime : `${usedTime}+`;
        const capped = trendCapSummary.count < trendCapSummary.total;
        const lead = capped ? `${trendCapSummary.count}/${trendCapSummary.total}` : `${trendCapSummary.count}`;
        // Count too-short flights across the full flight set, not the filtered
        // view — the cap note's other totals (count/total/hours) are unfiltered,
        // so a filter-scoped count here would understate it when filters apply.
        const tooShortCount = flightRows.filter(({ entry }) => isSupportedFlight(entry) && flightTooShortForTrendAnalysis(entry)).length;
        const tooShortNote = tooShortCount
          ? ` ${tooShortCount} flight${tooShortCount === 1 ? "" : "s"} under 15 minutes ${tooShortCount === 1 ? "is" : "are"} too short for Trends.`
          : "";
        trendCapNote.hidden = false;
        trendCapNote.textContent = `${lead} · ${hoursText}`;
        trendCapNote.title = `${capped ? `Trends use the last ${trendCapSummary.count} of ${trendCapSummary.total} included flights.` : `Trends use all ${trendCapSummary.count} included flight${trendCapSummary.count === 1 ? "" : "s"}.`} Limit: last ${trendFlightCap} flights or ${trendHoursCapLabel}, whichever comes first.${tooShortNote ? ` ${tooShortNote}` : ""}${trendCapSummary.hoursKnown ? "" : " Some flights have no recorded duration, so the hours total is a minimum."}`;
        trendCapNote.classList.toggle("is-capped", capped);
      }
    }
    const nextBodyHtml = sortedFlights.length
      ? sortedFlights.map(({ entry, durationLabel, summary }, rowIndex) => {
        const supported = isSupportedFlight(entry);
        const planLocked = flightPlanLocked(entry);
        const planLockMessage = String(entry?.database?.planAccess?.message ?? "This flight is retained but locked by the account plan.");
        const reportAvailable = flightReportAvailable(entry);
        const reportStale = reportAvailable && flightReportStale(entry);
        const aiSummaryAvailable = flightAiSummaryAvailable(entry);
        // No user-facing staleness (issue #364 decision 14): a stored summary
        // opens normally, with no stale wording, class, or tooltip.
        const aiSummaryCanRun = aiSummaryAvailable || aiSummaryAvailableForEntry(entry);
        const aiSummaryDisabled = planLocked || !aiSummaryCanRun || (readOnly && !aiSummaryAvailable);
        const aiSummaryLabel = planLocked
          ? "Locked"
          : aiSummaryAvailable
          ? "Open AI summary"
          : "Generate AI summary";
        const aiSummaryTitle = planLocked
          ? planLockMessage
          : aiSummaryAvailable
          ? `Open AI summary for ${flightDisplayTitle(entry)}`
          : readOnly
            ? readOnlyActionMessage("Generating AI summaries")
          : aiSummaryCanRun
            ? `Generate AI summary for ${flightDisplayTitle(entry)}`
            : "AI summaries require a completed analysis run";
        const reportLabel = planLocked
          ? "Locked"
          : reportAvailable
            ? reportStale ? "Open saved PDF report from an earlier analysis" : "Open PDF report"
            : "Generate PDF";
        const reportTitle = planLocked
          ? planLockMessage
          : reportAvailable
          ? reportStale
            ? `Open saved report from an earlier analysis for ${flightDisplayTitle(entry)}`
            : `Open report for ${flightDisplayTitle(entry)}`
          : readOnly
            ? readOnlyActionMessage("Generating PDF reports")
          : `Generate report for ${flightDisplayTitle(entry)}`;
        const reportDisabled = planLocked || !supported || (readOnly && !reportAvailable);
        const regenerateReportDisabled = planLocked || !supported || readOnly;
        const trendTooShort = supported && flightTooShortForTrendAnalysis(entry);
        const trendAutoExcluded = !trendTooShort && flightAutoExcludedFromTrendAnalysis(entry);
        const trendIncluded = flightIncludedInTrendAnalysis(entry);
        const trendOutsideWindow = trendIncluded && !flightConsumedByTrends(entry);
        const trendTitle = planLocked
          ? planLockMessage
          : supported
          ? trendTooShort
            ? "Flights under 15 minutes are too short to contribute reliable trend data and can't be added to Trends."
            : trendAutoExcluded
              ? "Excluded from trend analysis by default because this log does not show engine-run evidence"
              : trendOutsideWindow
                ? `Included, but older than the trend window — trends use only the last ${trendFlightCap} flights or ${trendHoursCapLabel}`
                // The included pill shows only its checkbox, so the tooltip is the
                // only place the word appears — keep it short and literal.
                : trendIncluded ? "Included" : "Excluded from trend analysis"
          : "Unsupported flights cannot be included in trend analysis";
        const trendClass = planLocked ? "locked" : trendTooShort ? "too-short" : trendIncluded ? "included" : trendAutoExcluded ? "skipped" : "excluded";
        const trendLabel = planLocked ? "Locked" : trendTooShort ? "Too short" : trendIncluded ? "Included" : trendAutoExcluded ? "Skipped" : "Excluded";
        const updateAction = flightUpdateAction(entry);
        const updateReprocessPending = updateAction?.kind === "reprocess"
          && setHas(aircraftUploadReprocessPending, entry?.database?.uploadId);
        const updateReprocessActive = updateReprocessPending
          && setHas(aircraftUploadReprocessActive, entry?.database?.uploadId);
        const updateActionLabel = updateReprocessActive
          ? "Updating..."
          : updateReprocessPending ? "Queued" : updateAction?.label;
        const canReanalyze = flightCanReanalyze(entry);
        // "current" means the saved run already matches the analyzer version
        // AND config hash, so a re-analysis would reproduce identical results.
        const analysisUpToDate = entry?.database?.compatibilityStatus?.status === "current";
        const displayTitle = flightDisplayTitle(entry);
        const dateStartLabel = flightDateStartLabel(entry);
        // original_filename and storage_key both come from the flight's uploads
        // join (flightListSelect), so entry.* already carries them — no need to
        // re-look-up the uploads-history cache (which the Flights tab never warms).
        const rawFilename = entry.original_filename ?? (entry.path ? entry.path.split("/").pop() : "") ?? "";
        const versionUpdateBadge = "";
        // Unreviewed flight: server-decorated per user, cleared by opening the
        // dashboard or the "Mark not new" bulk action. Part of the row markup
        // so the body's markup-identity render cache repaints when it changes.
        const newFlightTag = entry.is_new === true
          ? '<span class="row-new-tag" title="You have not opened this flight yet">New</span>'
          : "";
        const sourceStorageKey = entry.path ?? "";
        const sourceTitle = rawFilename
          ? `Source upload: ${rawFilename}${sourceStorageKey && sourceStorageKey !== rawFilename ? ` (${sourceStorageKey})` : ""}`
          : "Source upload is not available for this flight";
        const sourceFilenames = aircraftFlightSourceFilenames(entry);
        const monitorReference = aircraftFlightMonitorReference(entry);
        const sourceTooltipId = `aircraft-flight-source-${rowIndex}`;
        const sourceTooltipMarkup = sourceFilenames.length || monitorReference
          ? `<button type="button" class="aircraft-flight-display" aria-label="${escapeHtml(`Source details for ${dateStartLabel}`)}" aria-describedby="${escapeHtml(sourceTooltipId)}">
              <strong>${escapeHtml(dateStartLabel)}</strong>
              <span class="aircraft-flight-provenance-tooltip" id="${escapeHtml(sourceTooltipId)}" role="tooltip">
                ${sourceFilenames.length
                  ? `<span class="aircraft-flight-provenance-label">Original monitor ${sourceFilenames.length === 1 ? "file" : "files"}</span>
                    ${sourceFilenames.map((filename) => `<span class="aircraft-flight-provenance-value">${escapeHtml(filename)}</span>`).join("")}`
                  : ""}
                ${monitorReference
                  ? `<span class="aircraft-flight-provenance-label">Monitor flight reference</span>
                    <span class="aircraft-flight-provenance-value">${escapeHtml(monitorReference)}</span>`
                  : ""}
              </span>
            </button>`
          : `<strong class="aircraft-flight-display is-static">${escapeHtml(dateStartLabel)}</strong>`;
        const cgrSource = aircraftFlightCgrSource(entry, showCgrSource);
        const flightDeletePending = setHas(aircraftFlightDeletePending, entry.id);
        const flightSelectable = Boolean(entry.database?.flightId) && !readOnly && !flightDeletePending;
        const flightSelected = setHas(aircraftFlightBulkDeleteSelected, entry.id);
        // One definition, rendered into whichever cell currently owns it.
        const nameEditButton = planLocked || readOnly
          ? ""
          : `<button class="aircraft-flight-name-edit" data-aircraft-flight-name-edit="${escapeHtml(entry.id)}" type="button" title="${entry.custom_name ? "Edit flight name" : "Name this flight"}" aria-label="${escapeHtml(`Edit name for ${flightDisplayTitle(entry)}`)}"><span aria-hidden="true">✎</span></button>`;
        const rowClass = [
          supported ? "" : "is-unsupported",
          planLocked ? "is-plan-locked" : "",
          flightSelected ? "is-selected" : "",
        ].filter(Boolean).join(" ");
        return `
        <tr class="${escapeHtml(rowClass)}"${planLocked ? ` aria-disabled="true"` : ` data-aircraft-flight-row="${escapeHtml(entry.id)}"`}>
          <td class="aircraft-row-select-cell">
            <input data-aircraft-flight-select="${escapeHtml(entry.id)}" type="checkbox" aria-label="Select ${escapeHtml(displayTitle)} for bulk actions"${flightSelected ? " checked" : ""}${flightSelectable ? "" : " disabled"} />
          </td>
          <td data-flight-column="open">
            ${planLocked
              ? ""
              : `<button class="secondary-action compact-action aircraft-flight-open-action" data-aircraft-flight-open="${escapeHtml(entry.id)}" type="button" title="${escapeHtml(`Open ${flightDisplayTitle(entry)}`)}">Open</button>`}
          </td>
          <td data-flight-column="date">
            <div class="aircraft-flight-date">
              <span class="aircraft-flight-title-row">
                ${sourceTooltipMarkup}
                ${versionUpdateBadge}
                ${nameColumnVisible ? "" : nameEditButton}
                ${newFlightTag}
              </span>
              ${planLocked
                ? `<span class="aircraft-flight-inline-status is-plan-locked" title="${escapeHtml(planLockMessage)}">Plan limit</span>`
                : supported ? "" : '<span class="aircraft-flight-inline-status">Unsupported flight</span>'}
            </div>
          </td>
          <td data-flight-column="name" class="aircraft-flight-name-cell">
            <span class="aircraft-flight-name-slot" data-aircraft-flight-name-cell="${escapeHtml(entry.id)}">${entry.custom_name ? `<span class="aircraft-flight-custom-name">${escapeHtml(entry.custom_name)}</span>` : ""}</span>
            ${nameColumnVisible ? nameEditButton : ""}
          </td>
          <td data-flight-column="route" class="aircraft-flight-route-cell">${(() => {
            const display = flightRouteMarkup(entry, escapeHtml);
            if (planLocked) return `<span class="aircraft-flight-route is-plan-locked" title="${escapeHtml(planLockMessage)}">Plan limit</span>`;
            // Read-only accounts see the value only. Otherwise the cell is an edit
            // trigger: "＋ Add route" when blank, the route + a hover pencil when
            // set. Manual entry (issue #214) is handled in setup-navigation.
            if (readOnly) return display;
            if (flightRouteHasEndpoints(entry)) {
              return `<button class="aircraft-flight-route-edit" data-aircraft-flight-route-edit="${escapeHtml(entry.id)}" type="button" title="Edit From / To" aria-label="Edit origin and arrival">${display}<span class="aircraft-flight-route-pencil" aria-hidden="true">✎</span></button>`;
            }
            return `<button class="aircraft-flight-route-edit is-empty" data-aircraft-flight-route-edit="${escapeHtml(entry.id)}" type="button" title="Add From / To" aria-label="Add origin and arrival">＋ Add route</button>`;
          })()}</td>
          ${showCgrSource ? `
            <td data-flight-column="cgr">
              <span class="aircraft-flight-cgr-source ${escapeHtml(cgrSource?.className ?? "is-empty")}" title="${escapeHtml(cgrSource?.title ?? "No CGR source for this flight")}">${escapeHtml(cgrSource?.label ?? "--")}</span>
            </td>
          ` : ""}
          <td data-flight-column="duration"><span class="aircraft-flight-duration">${escapeHtml(durationLabel)}</span></td>
          <td data-flight-column="flags" class="aircraft-flight-flags-cell">${flightFlagSummaryMarkup(summary, entry)}</td>
          ${showAnalysisUpdate ? `
            <td data-flight-column="update" class="aircraft-flight-analysis-update-cell">
              ${updateAction?.kind === "reprocess"
                ? `<button
                    class="secondary-action aircraft-flight-analysis-update-action"
                    data-aircraft-flight-reprocess="${escapeHtml(entry.id)}"
                    type="button"
                    title="${escapeHtml(readOnly
                      ? readOnlyActionMessage("Updating flights")
                      : planLocked
                        ? planLockMessage
                        : updateReprocessPending ? "This flight update is already queued." : updateAction.title)}"
                    aria-label="${escapeHtml(`Update ${displayTitle}. ${updateAction.title}`)}"${readOnly || planLocked || updateReprocessPending ? " disabled" : ""}>
                    <span>${escapeHtml(updateActionLabel)}</span>
                    ${aircraftFlightReanalyzeIconMarkup()}
                  </button>`
                : updateAction?.kind === "reanalyze"
                  ? `<button
                    class="secondary-action aircraft-flight-analysis-update-action"
                    data-aircraft-flight-reanalyze="${escapeHtml(entry.id)}"
                    data-aircraft-flight-reanalyze-label="Update"
                    type="button"
                    title="${escapeHtml(readOnly
                      ? readOnlyActionMessage("Updating flights")
                      : planLocked
                        ? planLockMessage
                        : updateAction.title)}"
                    aria-label="${escapeHtml(`Update ${displayTitle}. ${updateAction.title}`)}"${readOnly || planLocked ? " disabled" : ""}>
                    <span>Update</span>
                    ${aircraftFlightReanalyzeIconMarkup()}
                  </button>`
                  : '<span class="aircraft-flight-analysis-update-empty" aria-label="No update available">—</span>'}
            </td>
          ` : ""}
          <td data-flight-column="trend" class="aircraft-flight-trend-cell">
            <label class="aircraft-flight-trend-toggle is-${escapeHtml(trendClass)}${trendOutsideWindow ? " is-outside-window" : ""}" title="${escapeHtml(trendTitle)}">
              <input data-aircraft-flight-trend="${escapeHtml(entry.id)}" type="checkbox"${trendIncluded ? " checked" : ""}${supported && !trendTooShort && !planLocked ? "" : " disabled"} />
              <span>${escapeHtml(trendLabel)}</span>
            </label>
            ${trendOutsideWindow ? `<span class="aircraft-flight-trend-window-note" title="${escapeHtml(`Trends use only the last ${trendFlightCap} flights or ${trendHoursCapLabel}. This flight is older than that window.`)}">Beyond trend window</span>` : ""}
          </td>
          <td data-flight-column="ai" class="aircraft-flight-ai-summary-cell">
            <button class="secondary-action aircraft-flight-ai-summary-action${planLocked ? "" : " is-icon-only"}${aiSummaryAvailable && !planLocked ? " has-summary" : ""}" data-aircraft-flight-ai-summary="${escapeHtml(entry.id)}" type="button" title="${escapeHtml(aiSummaryTitle)}" aria-label="${escapeHtml(aiSummaryLabel)}"${aiSummaryDisabled ? " disabled" : ""}>
              ${planLocked ? escapeHtml(aiSummaryLabel) : aircraftFlightAiSummaryIconMarkup()}
            </button>
            ${aiSummaryAvailable && !planLocked && flightAiSummaryUnviewed(entry)
              ? '<span class="row-new-tag ai-summary-new-tag" title="You have not viewed this AI summary yet">New</span>'
              : ""}
          </td>
          <td data-flight-column="report" class="aircraft-flight-report-cell">
            <div class="aircraft-flight-report-actions">
              <button class="secondary-action aircraft-flight-report-action${planLocked ? "" : " is-icon-only"}${reportAvailable && !planLocked ? " has-report" : ""}${reportStale && !planLocked ? " is-stale" : ""}" data-aircraft-flight-report="${escapeHtml(entry.id)}" type="button" title="${escapeHtml(reportTitle)}" aria-label="${escapeHtml(reportLabel)}"${reportDisabled ? " disabled" : ""}>
                ${planLocked ? escapeHtml(reportLabel) : aircraftFlightPdfIconMarkup()}
              </button>
              ${reportAvailable && !planLocked ? `
                <button class="secondary-action aircraft-flight-report-regenerate-action" data-aircraft-flight-report-regenerate="${escapeHtml(entry.id)}" type="button" title="${escapeHtml(readOnly ? readOnlyActionMessage("Regenerating PDF reports") : `Regenerate report for ${flightDisplayTitle(entry)}`)}" aria-label="${escapeHtml(`Regenerate PDF report for ${flightDisplayTitle(entry)}`)}"${regenerateReportDisabled ? " disabled" : ""}>
                  ${aircraftFlightRegenerateIconMarkup()}
                </button>
              ` : ""}
            </div>
          </td>
          <td data-flight-column="actions" class="aircraft-flight-actions-cell">
            <button class="secondary-action icon-action aircraft-flight-actions-ellipsis" data-aircraft-flight-actions-toggle="${escapeHtml(entry.id)}" type="button" aria-haspopup="true" aria-expanded="false" aria-label="More actions" title="More actions">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <circle cx="5" cy="12" r="1.6" fill="currentColor" stroke="none" />
                <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
                <circle cx="19" cy="12" r="1.6" fill="currentColor" stroke="none" />
              </svg>
            </button>
            <div class="aircraft-flight-actions">
              ${entry.database?.uploadId && rawFilename && !readOnly
                ? `<button class="secondary-action icon-action" data-upload-download="${escapeHtml(entry.database.uploadId)}" data-upload-filename="${escapeHtml(rawFilename)}" type="button" title="${escapeHtml(sourceTitle)}" aria-label="${escapeHtml(`Download raw log ${rawFilename}`)}">
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M12 3v11" />
                      <path d="m7 11 5 5 5-5" />
                      <path d="M5 20h14" />
                    </svg>
                  </button>`
                : ""}
              <button class="secondary-action icon-action aircraft-flight-reanalyze-action" data-aircraft-flight-reanalyze="${escapeHtml(entry.id)}" type="button" aria-label="Re-analyze ${escapeHtml(flightDisplayTitle(entry))}" title="${escapeHtml(
                readOnly
                  ? readOnlyActionMessage("Re-analyzing flights")
                  : planLocked
                    ? planLockMessage
                    : analysisUpToDate
                      ? `Force re-process ${flightDisplayTitle(entry)} (analysis is already up to date)`
                      : !canReanalyze
                        ? "Reprocess this flight with the current parser before re-analyzing."
                        : supported && entry.database?.flightId
                          ? `Re-analyze ${flightDisplayTitle(entry)}`
                          : "Only supported database-backed flights can be re-analyzed."
              )}" data-flight-up-to-date="${analysisUpToDate ? "1" : "0"}"${readOnly || planLocked || !supported || !entry.database?.flightId || !canReanalyze ? " disabled" : ""}>
                ${aircraftFlightReanalyzeIconMarkup()}
              </button>
              <button class="secondary-action icon-action" data-aircraft-flight-archive="${escapeHtml(entry.id)}" type="button" aria-label="Archive ${escapeHtml(flightDisplayTitle(entry))}" title="${escapeHtml(readOnly ? readOnlyActionMessage("Archiving flights") : flightDeletePending ? "Archiving flight..." : "Archive flight (moves it to the Archive tab in My Account)")}"${readOnly || flightDeletePending ? " disabled" : ""}>
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <rect x="3" y="4" width="18" height="4" rx="1" />
                  <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
                  <path d="M10 12h4" />
                </svg>
              </button>
            </div>
          </td>
          <td data-flight-column="spacer" aria-hidden="true"></td>
        </tr>
      `;
      }).join("")
      : `<tr><td colspan="${columnCount}">${escapeHtml(aircraft ? (flights.length ? "No individual flights match the current filters." : `No individual flights are available for ${aircraftDisplayLabel(aircraft)} yet. Upload an engine-log file to create flight records.`) : "No individual aircraft flights are available yet.")}</td></tr>`;
    // The generated markup already encodes every per-row state (sort, filters,
    // trend-included checkbox + class, flag summaries, report/AI button labels),
    // so it is itself the exact render key: skip the innerHTML rebuild when it
    // is byte-identical to the last assignment. This collapses the redundant
    // re-renders the async hydrate callbacks below trigger once flag-summary and
    // report metadata settle (each calls back into this function), and any no-op
    // render call — preserving the existing rows, their focus, and scroll.
    // Genuine state changes still produce different markup and a full rebuild.
    // The firstElementChild guard rebuilds if the body was externally cleared.
    // The route-editor guard rebuilds when an inline From/To editor is open
    // (its markup was injected directly, bypassing the cache), so cancelling or
    // saving an unchanged route restores the cell instead of leaving the editor
    // stuck on screen (issue #214).
    if (nextBodyHtml !== lastFlightsBodyHtml
      || !body.firstElementChild
      || body.querySelector("[data-aircraft-flight-route-editor]")
      || body.querySelector("[data-aircraft-flight-name-editor]")) {
      body.innerHTML = nextBodyHtml;
      lastFlightsBodyHtml = nextBodyHtml;
    }
    // Re-apply the column layout AFTER the body is written. The render-skip
    // above can leave existing rows in place, but a rebuilt body comes back
    // with every cell visible, so this has to run on both paths or hidden
    // columns flash back on.
    setupFlightColumnControls({ onRepaint: () => renderAircraftFlightsPanel(aircraft) });
    applyFlightColumns({ namedFlightsPresent: anyNamed });
    setupCompactFlightActions();
    syncCompactFlightActions();
    if (getSelectedAircraftPanel() === "flights" || document.querySelector("#aircraftFlightsPanel")?.classList.contains("is-active")) {
      hydrateAircraftFlightFlagSummaries(flightRows.map((row) => row.entry));
      hydrateAircraftFlightReportMetadata(flightRows.map((row) => row.entry), aircraft);
    }
  }

  // Update the uploads table body in place: reuse existing <tr> nodes when the
  // set of upload rows is unchanged, rewriting only rows whose generated markup
  // actually changed. This keeps the hovered <tr> node alive across the 1.5s
  // active-upload poll and progress updates, so :hover no longer flickers while
  // flights are processing. Falls back to a full rebuild when rows are
  // added/removed/reordered.
  function reconcileUploadRows(body, rows) {
    const existing = Array.from(body.querySelectorAll(":scope > tr[data-upload-row]"));
    const sameShape = existing.length === rows.length
      && existing.every((tr, index) => tr.getAttribute("data-upload-row") === rows[index].key);
    if (!sameShape) {
      body.innerHTML = rows
        .map((row) => `<tr data-upload-row="${escapeHtml(row.key)}" class="${row.className}">${row.innerHtml}</tr>`)
        .join("");
      Array.from(body.querySelectorAll(":scope > tr[data-upload-row]")).forEach((tr, index) => {
        tr.__uploadRowHtml = rows[index]?.innerHtml;
      });
      return;
    }
    rows.forEach((row, index) => {
      const tr = existing[index];
      if (tr.className !== row.className) tr.className = row.className;
      if (tr.__uploadRowHtml !== row.innerHtml) {
        tr.innerHTML = row.innerHtml;
        tr.__uploadRowHtml = row.innerHtml;
      }
    });
  }

  // Compact Actions mode: once the flights table genuinely overflows its
  // wrap, the three per-row action buttons collapse into one "..." button
  // whose dropdown reveals them — clawing back enough width that most
  // layouts stop needing the horizontal scroll at all.
  let compactFlightActionsBound = false;

  function syncCompactFlightActions() {
    // Node test harnesses render against minimal DOM stubs; every hop is
    // feature-checked so this stays a no-op there.
    const table = document.querySelector(".aircraft-flights-table");
    const wrap = typeof table?.closest === "function" ? table.closest(".table-wrap") : null;
    if (!table || !wrap || typeof table.classList?.toggle !== "function") return;
    // Measure at full width (class off) so the decision doesn't oscillate
    // with its own effect; this runs synchronously before paint.
    table.classList.remove("is-compact-actions");
    const needsCompact = wrap.scrollWidth > wrap.clientWidth + 1;
    table.classList.toggle("is-compact-actions", needsCompact);
    if (!needsCompact) closeOpenFlightActionMenus(table);
  }

  function closeOpenFlightActionMenus(table) {
    for (const row of table?.querySelectorAll?.("tr.is-actions-open") ?? []) {
      row.classList.remove("is-actions-open");
      row.querySelector("[data-aircraft-flight-actions-toggle]")?.setAttribute("aria-expanded", "false");
      const menu = row.querySelector(".aircraft-flight-actions");
      if (menu?.style) {
        menu.style.position = "";
        menu.style.top = "";
        menu.style.right = "";
      }
    }
  }

  function openFlightActionsMenu(row, toggle) {
    row.classList.add("is-actions-open");
    toggle.setAttribute("aria-expanded", "true");
    // position: fixed escapes the .table-wrap overflow clipping — the
    // absolute-positioned menu was cut off on the last rows. Anchored under
    // the toggle, flipped above it when the viewport has no room below.
    const menu = row.querySelector(".aircraft-flight-actions");
    if (!menu?.style || typeof toggle.getBoundingClientRect !== "function") return;
    const anchor = toggle.getBoundingClientRect();
    menu.style.position = "fixed";
    menu.style.right = `${Math.max(8, window.innerWidth - anchor.right)}px`;
    const menuHeight = menu.offsetHeight || 52;
    const below = anchor.bottom + 6;
    menu.style.top = below + menuHeight > window.innerHeight - 8
      ? `${Math.max(8, anchor.top - 6 - menuHeight)}px`
      : `${below}px`;
  }

  function setupCompactFlightActions() {
    if (compactFlightActionsBound) return;
    compactFlightActionsBound = true;
    if (typeof document?.addEventListener !== "function") return;
    const table = document.querySelector(".aircraft-flights-table");
    const wrap = typeof table?.closest === "function" ? table.closest(".table-wrap") : null;
    if (wrap && typeof ResizeObserver === "function") {
      new ResizeObserver(() => syncCompactFlightActions()).observe(wrap);
    }
    // A fixed-position menu does not track its anchor, so any scroll
    // (page, wrap, ancestors) closes it rather than letting it drift.
    document.addEventListener("scroll", () => {
      const openTable = document.querySelector(".aircraft-flights-table");
      if (openTable?.querySelector?.("tr.is-actions-open")) closeOpenFlightActionMenus(openTable);
    }, true);
    document.addEventListener("click", (event) => {
      const table = document.querySelector(".aircraft-flights-table");
      if (!table) return;
      const toggle = event.target?.closest?.("[data-aircraft-flight-actions-toggle]");
      if (toggle && table.contains(toggle)) {
        const row = toggle.closest("tr");
        const wasOpen = row?.classList.contains("is-actions-open");
        closeOpenFlightActionMenus(table);
        if (row && !wasOpen) openFlightActionsMenu(row, toggle);
        return;
      }
      // Clicking an action inside the open dropdown performs it and closes;
      // clicking anywhere else just closes.
      closeOpenFlightActionMenus(table);
    });
  }

  function renderAircraftUploadsPanel(aircraft) {
    const body = document.querySelector("#aircraftUploadsBody");
    const columnCount = 9;
    if (!body) return;
    const readOnly = appReadOnlyMode();
    if (!aircraft) {
      syncUploadReprocessSelectedButton();
      syncUploadBulkDeleteControls();
      uploadHistoryRefreshTimers.forEach((_, key) => clearUploadHistoryRefresh(key));
      body.innerHTML = `<tr><td colspan="${columnCount}">Select an aircraft to view uploads.</td></tr>`;
      return;
    }
    const uploads = aircraftUploadHistoryCache.get(aircraft);
    const error = aircraftUploadHistoryErrors.get(aircraft);
    if (!uploads && !aircraftUploadHistoryPending.has(aircraft)) {
      loadAircraftUploadHistory(aircraft).then(() => {
        if (selectedAircraftWorkspace() === aircraft) renderAircraftWorkspace();
      }).catch(() => {
        if (selectedAircraftWorkspace() === aircraft) renderAircraftUploadsPanel(aircraft);
      });
    }
    if (error && !uploads) {
      syncUploadReprocessSelectedButton({ hidden: true, disabled: true, title: error });
      syncUploadBulkDeleteControls({ hidden: true, disabled: true, title: error });
      body.innerHTML = `<tr><td colspan="${columnCount}">${escapeHtml(error)}</td></tr>`;
      return;
    }
    if (!uploads) {
      syncUploadReprocessSelectedButton({ hidden: true, disabled: true, title: "Upload history is still loading." });
      syncUploadBulkDeleteControls({ hidden: true, disabled: true, title: "Upload history is still loading." });
      body.innerHTML = `<tr><td colspan="${columnCount}">Loading upload history...</td></tr>`;
      return;
    }
    scheduleActiveUploadHistoryRefresh(aircraft, uploads);
    // Which uploads this device has not seen yet. Computed once per render;
    // on this aircraft's first ever render everything present is seeded as
    // seen, so an established account does not light up on rollout.
    const newUploadIdSet = newUploadIds(aircraft, uploads.map((upload) => ({
      id: String(upload?.id ?? ""),
      uploadedAt: upload?.uploaded_at,
    })));
    setAircraftUploadsHaveNew(aircraft, newUploadIdSet.size > 0);
    const reprocessableUploads = uploads.filter((upload) => uploadCanReprocess(upload, {
      readOnly,
      reprocessPending: aircraftUploadReprocessPending.has(upload.id),
      deletePending: aircraftUploadDeletePending.has(upload.id),
    }));
    const selectableUploadIds = uploads
      .filter((upload) => upload?.id && !setHas(aircraftUploadDeletePending, upload.id))
      .map((upload) => upload.id);
    const selectedUploadCount = selectableUploadIds.filter((id) => setHas(aircraftUploadBulkDeleteSelected, id)).length;
    const selectedReprocessableCount = reprocessableUploads.filter((upload) => setHas(aircraftUploadBulkDeleteSelected, upload.id)).length;
    syncUploadReprocessSelectedButton({
      hidden: selectedReprocessableCount === 0,
      disabled: readOnly || selectedReprocessableCount === 0,
      count: selectedReprocessableCount,
      title: readOnly
        ? readOnlyActionMessage("Reprocessing uploads")
        : selectedReprocessableCount
          ? `Queue ${selectedReprocessableCount} selected upload${selectedReprocessableCount === 1 ? "" : "s"} for reprocessing.`
          : reprocessableUploads.length
            ? "Select uploads to enable reprocessing."
            : "No uploads are available to reprocess.",
    });
    const uploadDeleteBusy = uploads.some((upload) => setHas(aircraftUploadDeletePending, upload.id));
    syncUploadBulkDeleteControls({
      hidden: selectedUploadCount === 0,
      disabled: readOnly || uploadDeleteBusy || selectedUploadCount === 0,
      selectAllDisabled: readOnly || uploadDeleteBusy,
      selectedCount: selectedUploadCount,
      selectableCount: selectableUploadIds.length,
      title: readOnly
        ? readOnlyActionMessage("Deleting uploads")
        : uploadDeleteBusy
          ? "Upload deletion is already running."
          : selectedUploadCount
            ? `Delete ${selectedUploadCount} selected upload${selectedUploadCount === 1 ? "" : "s"} and every record created from ${selectedUploadCount === 1 ? "it" : "them"}.`
            : selectableUploadIds.length
              ? "Select uploads to enable bulk delete."
              : "No uploads can be deleted.",
    });
    const uploadRowDescriptors = uploads.length
      ? uploads.map((upload) => {
        const status = uploadDisplayStatus(upload);
        const job = upload.latest_ingest_job ?? null;
        const jobError = uploadDisplayError(upload, job);
        const reprocessPending = aircraftUploadReprocessPending.has(upload.id);
        const reprocessActive = aircraftUploadReprocessActive.has(upload.id);
        const processShortPending = aircraftUploadProcessShortPending.has(upload.id);
        const deletePending = aircraftUploadDeletePending.has(upload.id);
        const activeJob = uploadHasActiveJob(upload);
        const emptyUpload = uploadIsEmpty(upload);
        const canReprocess = uploadCanReprocess(upload, {
          readOnly,
          reprocessPending,
          deletePending,
        });
        const flightSummary = uploadFlightSummary(upload);
        const flightSummaryDetailClass = uploadFlightSummaryDetailClass(upload);
        const shortFlightCount = Number(upload.short_flight_count ?? 0);
        const actionLabel = uploadReprocessActionLabel(upload, { reprocessPending, reprocessActive });
        const rawFilename = upload.original_filename ?? "Raw upload";
        const pairedUpload = upload.paired_upload ?? upload.cgr_pairing?.pairedUpload ?? null;
        const uploadSelected = setHas(aircraftUploadBulkDeleteSelected, upload.id);
        const uploadSelectable = Boolean(upload.id) && !readOnly && !deletePending;
        const uploadIsNew = newUploadIdSet.has(String(upload.id ?? ""));
        return {
          key: String(upload.id ?? ""),
          className: uploadSelected ? "is-selected" : "",
          innerHtml: `
            <td class="aircraft-row-select-cell">
              <input data-aircraft-upload-select="${escapeHtml(upload.id)}" type="checkbox" aria-label="${escapeHtml(`Select upload ${rawFilename} for bulk actions`)}"${uploadSelected ? " checked" : ""}${uploadSelectable ? "" : " disabled"} />
            </td>
            <td${uploadIsNew ? ' data-upload-new-cell="1"' : ""}>
              <div class="aircraft-upload-file">
                <span class="aircraft-upload-file-name">
                  ${upload.id && !readOnly
                    ? `<button type="button" class="aircraft-upload-download" data-upload-download="${escapeHtml(upload.id)}" data-upload-filename="${escapeHtml(rawFilename)}">${escapeHtml(rawFilename)}</button>`
                    : `<strong>${escapeHtml(rawFilename)}</strong>`}
                  ${uploadIsNew ? '<span class="row-new-tag" title="Added since you last looked">New</span>' : ""}
                </span>
                <span>${escapeHtml(upload.storage_key ?? "")}</span>
                ${pairedUpload ? `<span>${escapeHtml(`Paired raw file: ${pairedUpload.original_filename ?? pairedUpload.storage_key ?? "CGR-30C"}`)}</span>` : ""}
              </div>
            </td>
            <td>
              <span class="aircraft-upload-status aircraft-upload-status-${escapeHtml(uploadStatusClass(status))}">
                ${escapeHtml(uploadStatusLabel(status))}
              </span>
              ${upload.error_message ? `<small class="aircraft-upload-error">${escapeHtml(upload.error_message)}</small>` : ""}
            </td>
            <td>
              <div class="aircraft-upload-flight-summary">
                <strong>${escapeHtml(flightSummary.label)}</strong>
                ${flightSummary.detail ? `<span class="aircraft-upload-flight-summary-detail ${escapeHtml(flightSummaryDetailClass)}">${escapeHtml(flightSummary.detail)}</span>` : ""}
              </div>
            </td>
            <td>
              <span>${escapeHtml(uploadJobProgressLabel(job, upload))}</span>
              ${jobError ? `<small class="aircraft-upload-error">${escapeHtml(jobError)}</small>` : ""}
            </td>
            <td>${escapeHtml(formatUploadByteSize(upload.byte_size))}</td>
            <td>${escapeHtml(uploadDateLabel(upload))}</td>
            <td><code>${escapeHtml(shortUploadHash(upload))}</code></td>
            <td>
              <div class="aircraft-upload-actions">
                <button class="secondary-action aircraft-upload-action" data-upload-reprocess="${escapeHtml(upload.id)}" type="button" title="${escapeHtml(readOnly ? readOnlyActionMessage("Reprocessing uploads") : "")}"${canReprocess ? "" : " disabled"}>
                  ${escapeHtml(actionLabel)}
                </button>
                ${shortFlightCount > 0
                  ? `<button class="secondary-action aircraft-upload-action aircraft-upload-action-short" data-upload-process-short="${escapeHtml(upload.id)}" type="button" title="${escapeHtml(readOnly ? readOnlyActionMessage("Processing short flights") : `Analyze ${shortFlightCount} flight${shortFlightCount === 1 ? "" : "s"} skipped for being under 10 minutes`)}"${readOnly || activeJob || processShortPending || deletePending ? " disabled" : ""}>
                  ${escapeHtml(processShortPending ? "Queued..." : "Process Short Flights")}
                </button>`
                  : ""}
                <button class="secondary-action danger-action icon-action aircraft-upload-delete-action" data-upload-delete="${escapeHtml(upload.id)}" type="button" aria-label="${escapeHtml(`Delete upload ${rawFilename}`)}" title="${escapeHtml(readOnly ? readOnlyActionMessage("Deleting uploads") : "Delete upload and all records")}"${readOnly || deletePending ? " disabled" : ""}>
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M3 6h18" />
                    <path d="M8 6V4h8v2" />
                    <path d="M19 6l-1 14H6L5 6" />
                    <path d="M10 11v5" />
                    <path d="M14 11v5" />
                  </svg>
                </button>
              </div>
            </td>
          `,
        };
      })
      : null;
    if (!uploadRowDescriptors) {
      body.innerHTML = `<tr><td colspan="${columnCount}">${escapeHtml(`No uploaded engine-log files found for ${aircraftDisplayLabel(aircraft)} yet.`)}</td></tr>`;
      return;
    }
    reconcileUploadRows(body, uploadRowDescriptors);
  }

  return {
    renderAircraftWorkspace,
    fleetRowsFromDom,
    clearAircraftFleetDragState,
    aircraftRowDropPlacement,
    persistAircraftFleetOrderFromDom,
    reorderAircraftFleetRow,
    aircraftFlightAiSummaryIconMarkup,
    aircraftFlightPdfIconMarkup,
    renderAircraftFlightsPanel,
    renderAircraftUploadsPanel,
  };
}
